import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  Tooltip,
  Fab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SearchIcon from "@mui/icons-material/Search";
import DraftsIcon from "@mui/icons-material/Drafts";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import SettingsIcon from "@mui/icons-material/Settings";
import { Helmet } from "react-helmet";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import SectionLoader from "../components/SectionLoader";
import { getDisplayRoleName } from "../utils/roleUtils";
import DraftManager from "../utils/draftManager";
import ArchiveManager from "../utils/archiveManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ModuleSettingsModal from "../components/ModuleSettingsModal";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formValue, setFormValue] = useState("");
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [roleId, setRoleId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';

  // Drafts state
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('roles'));
  const [activeDraftId, setActiveDraftId] = useState(null);

  // Archive state
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('roles'));
  const [filterActiveTab, setFilterActiveTab] = useState('active'); // 'active' or 'archived'

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setToast({ open: true, message: data.message || 'Failed to fetch roles', severity: "error" });
      } else {
        setRoles(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setToast({ open: true, message: 'Network error fetching roles', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleOpen = (role = null) => {
    setEditing(role);
    setRoleId(role ? role.id : null);
    setFormValue(role ? role.role : "");
    setActiveDraftId(null);
    setOpen(true);
  };

  const hasFormData = Boolean(formValue.trim());

  const handleSaveDraft = (showToast = true) => {
    if (!formValue.trim()) {
      if (showToast) setToast({ open: true, message: "Nothing to save as draft", severity: "warning" });
      return;
    }
    const currentDrafts = DraftManager.getDrafts('roles');
    if (!activeDraftId && currentDrafts.length >= 3) {
      if (showToast) setToast({ open: true, message: "Maximum 3 drafts allowed. Please delete or resume an existing draft first.", severity: "warning" });
      return;
    }
    const draftData = {
      role: formValue,
      editingId: editing ? editing.id : null,
    };
    const title = `Role: ${formValue.trim()}`;
    const saved = DraftManager.saveDraft('roles', draftData, title, activeDraftId);
    if (saved) {
      setActiveDraftId(saved.id);
      setDraftsList(DraftManager.getDrafts('roles'));
      if (showToast) setToast({ open: true, message: "Draft saved successfully (Max 3)", severity: "success" });
    }
  };

  const handleResumeDraft = (draft) => {
    if (!draft || !draft.data) return;
    setActiveDraftId(draft.id);
    setFormValue(draft.data.role || "");
    setEditing(draft.data.editingId ? { id: draft.data.editingId } : null);
    setRoleId(draft.data.editingId || null);
    setOpen(true);
    setDraftsOpen(false);
    setToast({ open: true, message: "Draft loaded into form", severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    DraftManager.deleteDraft('roles', draftId);
    setDraftsList(DraftManager.getDrafts('roles'));
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft deleted", severity: "info" });
  };

  // Auto-save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (open && formValue.trim()) {
        const currentDrafts = DraftManager.getDrafts('roles');
        if (activeDraftId || currentDrafts.length < 3) {
          const draftData = { role: formValue };
          DraftManager.saveDraft('roles', draftData, `Role: ${formValue.trim()}`, activeDraftId);
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, formValue, activeDraftId]);

  const handleClose = () => {
    if (hasFormData && !editing) {
      handleSaveDraft(false);
    }
    setOpen(false);
    setEditing(null);
    setFormValue("");
    setActiveDraftId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (editing) {
      try {
        const response = await fetch(`${API_URL}update-role`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: formValue.trim(),
            roleId: roleId
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
          setToast({ open: true, message: data.message || 'Failed to update role', severity: "error" });
        } else {
          setToast({ open: true, message: data.message || 'Role updated successfully', severity: "success" });
          if (activeDraftId) {
            DraftManager.deleteDraft('roles', activeDraftId);
            setActiveDraftId(null);
            setDraftsList(DraftManager.getDrafts('roles'));
          }
          fetchRoles();
          handleClose();
        }
      } catch (err) {
        setToast({ open: true, message: 'Network error', severity: 'error' });
      }
    } else {
      try {
        const response = await fetch(`${API_URL}add-role`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: formValue.trim()
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
          setToast({ open: true, message: data.message || 'Failed to add role', severity: "error" });
        } else {
          setToast({ open: true, message: data.message || 'Role created successfully', severity: "success" });
          if (activeDraftId) {
            DraftManager.deleteDraft('roles', activeDraftId);
            setActiveDraftId(null);
            setDraftsList(DraftManager.getDrafts('roles'));
          }
          fetchRoles();
          handleClose();
        }
      } catch (err) {
        setToast({ open: true, message: 'Network error', severity: 'error' });
      }
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Role',
      message: 'Are you sure you want to delete this role? Users assigned to this role may lose access permissions.',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });

        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}delete-role`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              roleId: id
            })
          });
          const data = await response.json();

          if (!response.ok || data.success === false) {
            if (data.message === 'Invalid token') {
              localStorage.removeItem('token');
              window.location.href = '/login';
              return;
            }
            setToast({ open: true, message: data.message || 'Delete failed', severity: "error" });
          } else {
            setToast({ open: true, message: data.message || 'Role deleted successfully', severity: "success" });
            fetchRoles();
          }
        } catch (err) {
          setToast({ open: true, message: 'Network error deleting role', severity: 'error' });
        }
      }
    });
  };

  // Archive Actions
  const handleArchive = (role) => {
    ArchiveManager.archiveItem('roles', role);
    setArchivedList(ArchiveManager.getArchivedItems('roles'));
    setToast({ open: true, message: `"${role.role}" archived and hidden from selection`, severity: 'info' });
  };

  const handleRestore = (roleId) => {
    ArchiveManager.restoreItem('roles', roleId);
    setArchivedList(ArchiveManager.getArchivedItems('roles'));
    setToast({ open: true, message: "Role restored to active list", severity: 'success' });
  };

  const handleDeletePermanently = (roleId) => {
    ArchiveManager.deletePermanently('roles', roleId);
    setArchivedList(ArchiveManager.getArchivedItems('roles'));
    setToast({ open: true, message: "Archived role permanently deleted", severity: 'success' });
  };

  const activeRoles = roles.filter(
    (r) => !archivedList.some((a) => String(a.id) === String(r.id))
  );
  const currentList = filterActiveTab === 'archived' ? archivedList : activeRoles;

  const filteredRoles = currentList.filter((r) => {
    const term = search.toLowerCase();
    const displayRole = getDisplayRoleName(r.role).toLowerCase();
    const rawRole = (r.role || "").toLowerCase();
    return displayRole.includes(term) || rawRole.includes(term);
  });

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Helmet>
        <title>Roles - GPN</title>
      </Helmet>

      {/* Header Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)",
          borderRadius: 3,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: "white",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: "rgba(76, 161, 175, 0.2)",
              border: "1px solid rgba(76, 161, 175, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4CA1AF",
            }}
          >
            <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
              Role Management
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              Define user roles and organization authority levels across the platform.
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<DraftsIcon />}
            onClick={() => setDraftsOpen(true)}
            sx={{
              color: draftsList.length > 0 ? "#64d2e2" : "rgba(255,255,255,0.7)",
              borderColor: draftsList.length > 0 ? "#4CA1AF" : "rgba(255,255,255,0.3)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 1,
              "&:hover": { borderColor: "#64d2e2", backgroundColor: "rgba(76, 161, 175, 0.1)" },
            }}
          >
            Drafts ({draftsList.length}/3)
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            data-testid="add-role-btn"
            sx={{
              background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 1,
              boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
              },
            }}
          >
            Add Role
          </Button>
        </Box>
      </Box>

      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />

      {/* Table & Controls Card */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="h6" fontWeight={700} color="#FFFFFF">
              {filterActiveTab === 'archived' ? 'Archived Roles' : 'System Roles'}
            </Typography>

            <Button
              size="small"
              variant={filterActiveTab === "active" ? "contained" : "outlined"}
              onClick={() => setFilterActiveTab("active")}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: filterActiveTab === "active" ? "#4CA1AF" : "transparent",
                color: filterActiveTab === "active" ? "#FFFFFF" : "rgba(255,255,255,0.7)",
                borderColor: "rgba(76, 161, 175, 0.4)",
                "&:hover": {
                  backgroundColor: filterActiveTab === "active" ? "#3d8b99" : "rgba(76, 161, 175, 0.1)",
                }
              }}
            >
              Active ({activeRoles.length})
            </Button>

            <Button
              size="small"
              variant={filterActiveTab === "archived" ? "contained" : "outlined"}
              startIcon={<InventoryIcon sx={{ fontSize: 16 }} />}
              onClick={() => setFilterActiveTab("archived")}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: filterActiveTab === "archived" ? "#F59E0B" : "transparent",
                color: filterActiveTab === "archived" ? "#FFFFFF" : "rgba(255,255,255,0.7)",
                borderColor: "rgba(245, 158, 11, 0.4)",
                "&:hover": {
                  backgroundColor: filterActiveTab === "archived" ? "#D97706" : "rgba(245, 158, 11, 0.1)",
                }
              }}
            >
              Archived ({archivedList.length})
            </Button>
          </Box>

          <TextField
            size="small"
            placeholder="Search role name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.6)" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                color: "#FFFFFF",
                background: "rgba(255, 255, 255, 0.05)",
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                "&:hover fieldset": { borderColor: "#4CA1AF" },
                "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
              },
            }}
          />
        </Box>

        {loading ? (
          <SectionLoader />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "rgba(76, 161, 175, 0.12)" }}>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 80 }}>#</TableCell>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700 }}>Role Name</TableCell>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 140 }}>Internal Key</TableCell>
                  <TableCell align="right" sx={{ color: "#4CA1AF", fontWeight: 700, width: 140 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.map((role, idx) => (
                  <TableRow
                    key={role.id}
                    data-testid="role-row"
                    onClick={() => {
                      if (filterActiveTab !== 'archived') {
                        handleOpen(role);
                      }
                    }}
                    sx={{
                      cursor: filterActiveTab !== 'archived' ? 'pointer' : 'default',
                      "&:hover": { background: "rgba(76, 161, 175, 0.12)" },
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 18, color: "#4CA1AF", opacity: 0.8 }} />
                        {getDisplayRoleName(role.role)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.role}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          backgroundColor: "rgba(76, 161, 175, 0.15)",
                          color: "#4CA1AF",
                          border: "1px solid rgba(76, 161, 175, 0.3)",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {filterActiveTab === 'archived' ? (
                        <>
                          <Tooltip title="Restore to Active Roles">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleRestore(role.id); }}
                              sx={{
                                color: "#10B981",
                                mr: 1,
                                "&:hover": { background: "rgba(16, 185, 129, 0.15)" },
                              }}
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Forever">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeletePermanently(role.id); }}
                              sx={{
                                color: "#EF4444",
                                "&:hover": { background: "rgba(239, 68, 68, 0.15)" },
                              }}
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit Role">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleOpen(role); }}
                              data-testid={`edit-role-btn-${role.id}`}
                              sx={{
                                color: "#4CA1AF",
                                mr: 0.5,
                                "&:hover": { background: "rgba(76, 161, 175, 0.15)" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive (Hide from Selection)">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleArchive(role); }}
                              sx={{
                                color: "#F59E0B",
                                mr: 0.5,
                                "&:hover": { background: "rgba(245, 158, 11, 0.15)" },
                              }}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Role">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDelete(role.id); }}
                              data-testid={`delete-role-btn-${role.id}`}
                              sx={{
                                color: "#EF4444",
                                "&:hover": { background: "rgba(239, 68, 68, 0.15)" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRoles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "rgba(255, 255, 255, 0.5)", py: 4 }}>
                      {filterActiveTab === 'archived'
                        ? "No archived roles found."
                        : 'No roles found. Click "Add Role" to create a new role.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: "#1E293B",
            color: "#FFFFFF",
            borderRadius: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid rgba(255, 255, 255, 0.1)", pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{editing ? "Edit Role" : "Add New Role"}</span>
          {activeDraftId && (
            <Chip label="Editing Draft" size="small" sx={{ bgcolor: "rgba(100, 210, 226, 0.2)", color: "#64d2e2" }} />
          )}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)", fontWeight: 600 }}>Role Name *</Typography>
                <HelpTooltip
                  title="System Role"
                  purpose="Defines access privileges and authorization tiers across the platform."
                  howItWorks="Enter a distinct role descriptor (e.g. Prayer Coordinator, Senior Pastor, Moderator)."
                  mobileEffect="Governs which features and administrative controls the user can see in the mobile app."
                />
              </Box>
              <TextField
                autoFocus
                placeholder="e.g. Moderator, Assistant"
                type="text"
                fullWidth
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                required
                InputProps={{
                  sx: {
                    color: "#FFFFFF",
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    "&:hover fieldset": { borderColor: "#4CA1AF" },
                    "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid rgba(255, 255, 255, 0.1)", justifyContent: "space-between" }}>
            <Box>
              <Button
                onClick={() => handleSaveDraft(true)}
                startIcon={<DraftsIcon />}
                sx={{ color: "#64d2e2", textTransform: "none", fontWeight: 600 }}
              >
                Save Draft
              </Button>
            </Box>
            <Box display="flex" gap={1}>
              <Button onClick={handleClose} sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {editing ? "Save Changes" : "Create"}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Drafts Modal */}
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        drafts={draftsList}
        onResumeDraft={handleResumeDraft}
        onDeleteDraft={handleDeleteDraft}
      />

      {/* Floating Settings FAB */}
      <Tooltip title="Roles Settings">
        <Fab
          color="primary"
          size="medium"
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1200,
            background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
            boxShadow: "0 6px 20px rgba(76, 161, 175, 0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
            },
          }}
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>
      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey="roles"
        moduleTitle="Roles"
      />
    </Box>
  );
};

export default Roles;